import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Typography,
  Tooltip,
} from '@mui/material';

const PartsSelector = ({ scope, onPartChange, updateScope }) => {
  const selectRefs = useRef({});
  const [hasInitialized, setHasInitialized] = useState(false);

  const initializeScopeAssignedParts = useCallback(() => {
    if (scope.partList && scope.partList.length > 0 && !hasInitialized) {
      const autoIncludedParts = scope.partList
        .filter(part => part.scope_assigned === true)
        .map(part => part.bom_id);
      
      if (autoIncludedParts.length > 0) {
        const currentSelection = scope.parts || [];
        const newAutoIncluded = autoIncludedParts.filter(id => !currentSelection.includes(id));
        
        if (newAutoIncluded.length > 0) {
          const newSelection = [...new Set([...currentSelection, ...newAutoIncluded])];
          onPartChange(newSelection);
        }
      }
      setHasInitialized(true);
    }
  }, [scope.partList, scope.parts, onPartChange, hasInitialized]);

  useEffect(() => {
    initializeScopeAssignedParts();
  }, [initializeScopeAssignedParts]);

  useEffect(() => {
    setHasInitialized(false);
  }, [scope.id]);

  useEffect(() => {
    if (scope?.partList?.length) {
      const partValue = scope.partList
        .filter(item => item?.scope_assigned)
        .map(item => item.bom_id);
  
      if (partValue.length > 0) {
        updateScope(scope.id, { parts: partValue });
      }
    } 
    // eslint-disable-next-line
  }, [scope.partList]);
  

  const getPartsDisplayValue = (scope) => {
    if (!scope.editing && scope.existingPartsDisplay?.length > 0) {
      return scope.existingPartsDisplay;
    }
    return scope.parts || [];
  };

  const isPartDisabled = (part, isNewScope = false) => {
    return part.scope_assigned === false && part.fully_assigned === true;
  };

  const isPartChecked = (part, selectedParts) => {
    return selectedParts && selectedParts.includes(part.bom_id);
  };

  const getSelectableParts = (partList, isNewScope = false) => {
    if (isNewScope) {
      return partList.filter(part => !isPartDisabled(part, isNewScope));
    } else {
      return partList;
    }
  };

  const calculateVisibleChips = (scopeId, selectedBomIds, partList) => {
    if (!selectedBomIds || selectedBomIds.length === 0) return { visibleItems: [], remainingCount: 0 };

    const selectElement = selectRefs.current[scopeId];
    if (!selectElement) return { visibleItems: selectedBomIds.slice(0, 3), remainingCount: Math.max(0, selectedBomIds.length - 3) };

    const containerWidth = selectElement.offsetWidth - 32;
    const overflowChipWidth = 45;
    const chipGap = 4;

    let totalWidth = 0;
    let visibleCount = 0;

    for (let i = 0; i < selectedBomIds.length; i++) {
      const bomId = selectedBomIds[i];
      const part = partList.find(p => p.bom_id === bomId);
      const partName = part ? part.part_name : bomId.toString();

      const estimatedChipWidth = Math.min(Math.max(partName.length * 8 + 24, 60), 200);

      const widthWithThisChip = totalWidth + estimatedChipWidth + chipGap;
      const wouldNeedOverflow = i < selectedBomIds.length - 1;
      const totalNeededWidth = widthWithThisChip + (wouldNeedOverflow ? overflowChipWidth + chipGap : 0);

      if (totalNeededWidth <= containerWidth) {
        totalWidth = widthWithThisChip;
        visibleCount = i + 1;
      } else {
        break;
      }
    }

    if (visibleCount === 0 && selectedBomIds.length > 0) {
      visibleCount = 1;
    }

    return {
      visibleItems: selectedBomIds.slice(0, visibleCount),
      remainingCount: selectedBomIds.length - visibleCount
    };
  };

  const renderPartsValue = (scope, selectedBomIds) => {
    if (!selectedBomIds || selectedBomIds.length === 0) {
      if (!scope.editing && scope.existingPartsDisplay?.length > 0) {
        return 'Previously selected parts';
      }
      return '';
    }

    const { visibleItems, remainingCount } = calculateVisibleChips(scope.id, selectedBomIds, scope.partList || []);

    return (
      <Box sx={{
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
        overflow: 'hidden',
        width: '100%'
      }}>
        {visibleItems.map((bomId) => {
          const part = scope.partList?.find(p => p.bom_id === bomId);
          return (
            <Chip
              key={bomId}
              label={part ? part.part_name : bomId}
              size="small"
              sx={{
                flexShrink: 0,
                maxWidth: '200px',
                backgroundColor: '#fef3f2',
                color: "black",
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }}
            />
          );
        })}
        {remainingCount > 0 && (
          <Tooltip
            title={
              <Box>
                {selectedBomIds.slice(visibleItems.length).map((bomId, index) => {
                  const part = scope.partList?.find(p => p.bom_id === bomId);
                  const partName = part ? part.part_name : bomId;
                  return (
                    <Typography 
                      key={bomId} 
                      variant="caption" 
                      component="div"
                      sx={{ 
                        color: 'black',
                        fontSize: '0.75rem',
                        lineHeight: 1.2,
                        marginBottom: index < selectedBomIds.slice(visibleItems.length).length - 1 ? '2px' : 0
                      }}
                    >
                      {partName}
                    </Typography>
                  );
                })}
              </Box>
            }
            placement="bottom"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: 'white',
                  maxWidth: '300px',
                  padding: '8px 12px',
                  fontSize: '0.75rem',
                  border: '1px solid #ccc',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                }
              },
              arrow: {
                sx: {
                  color: 'white',
                  '&::before': {
                    border: '1px solid #ccc'
                  }
                }
              }
            }}
          >
            <Chip
              label={`+${remainingCount}`}
              size="small"
              variant="outlined"
              sx={{
                flexShrink: 0,
                minWidth: 'auto',
                width: 'auto',
                cursor: 'pointer'
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const isComponentDisabled = !scope.editing || !scope.isPartsEnabled || scope.isLoadingParts || scope.isLoadingDropdownData;

const handleSelectChange = useCallback((e) => {
  const value = e.target.value;
  const isNewScope = scope.isNewRow;
  const selectableParts = getSelectableParts(scope.partList || [], isNewScope);
      
  if (value.includes('SELECT_ALL')) {
    const allSelectableBomIds = selectableParts
      .filter(part => !isPartDisabled(part, isNewScope))
      .map(part => part.bom_id);
    
    const currentSelection = scope.parts || [];
    const currentManuallySelected = currentSelection.filter(id => {
      const part = scope.partList?.find(p => p.bom_id === id);
      return part && !part.scope_assigned;
    });
    const selectableSelected = allSelectableBomIds.filter(id => currentManuallySelected.includes(id));
    
    if (selectableSelected.length === allSelectableBomIds.length) {
      const scopeAssignedParts = scope.partList
        ?.filter(part => part.scope_assigned === true)
        .map(part => part.bom_id) || [];
      
      const nonSelectableParts = currentSelection.filter(id => {
        const part = scope.partList?.find(p => p.bom_id === id);
        return part && !allSelectableBomIds.includes(id) && !part.scope_assigned;
      });
      
      const finalSelection = [...new Set([...scopeAssignedParts, ...nonSelectableParts])];
      onPartChange(finalSelection);
    } else {
      const allSelected = [...new Set([...allSelectableBomIds, ...currentSelection])];
      onPartChange(allSelected);
    }
  } else {
    const filteredValue = value.filter(v => v !== 'SELECT_ALL');
  
    onPartChange(filteredValue);
  }
  // eslint-disable-next-line
}, [scope.partList, scope.parts, scope.isNewRow, onPartChange]);


const renderSelectValue = (selectedBomIds) => {
  if (!scope.editing) {
    let totalCount = 0;
    
    if (scope.existingPartsDisplay?.length > 0) {
      totalCount = scope.existingPartsDisplay.length;
    } else if (selectedBomIds && selectedBomIds.length > 0) {
      totalCount = selectedBomIds.length;
    } else {
      const fullyAssignedCount = scope.partList?.filter(p => p.scope_assigned).length || 0;
      totalCount = fullyAssignedCount;
    }
    
    if (totalCount === 0 && scope.trackerCount) {
      totalCount = scope.trackerCount;
    }
    
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#f16253',
          fontSize: 16
        }}
      >
        {totalCount > 0 ? `${totalCount} part(s) selected` : "No parts selected"}
      </Typography>
    );
  }

  const hasSelectedParts = selectedBomIds && selectedBomIds.length > 0;

  if (!hasSelectedParts) {
    return (
      <span 
        style={{ 
          color: '#f16253',
          fontSize: 16 
        }}
      >
        Select parts...
      </span>
    );
  }

  if (!scope.partList || scope.partList.length === 0 || scope.isLoadingDropdownData) {
    return (
      <Typography 
        variant="body2" 
        sx={{ 
          color: '#f16253',
          fontSize: 16
        }}
      >
        Loading parts...
      </Typography>
    );
  }

  return renderPartsValue(scope, selectedBomIds);
  };

  return (
    <Box>
      <FormControl 
        sx={{ 
          width: '100%',
          cursor: isComponentDisabled ? 'not-allowed' : 'pointer',
          '& *': {
            cursor: isComponentDisabled ? 'not-allowed' : 'pointer',
          }
        }} 
        size="small"
      >
        <Select
          ref={el => selectRefs.current[scope.id] = el}
          labelId={`select-label-${scope.id}`}
          fullWidth
          multiple
          value={getPartsDisplayValue(scope)}
          onChange={handleSelectChange}
          disabled={isComponentDisabled}
          displayEmpty
          sx={{
            cursor: isComponentDisabled ? 'not-allowed' : 'pointer',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f16253 !important',
              borderWidth: '1px !important'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f16253 !important',
              borderWidth: '1px !important'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f16253 !important',
              borderWidth: '1px !important'
            },
            '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
              borderColor: '#f16253 !important',
              borderWidth: '1px !important'
            },
            '& .MuiSelect-select': {
              color: '#f16253 !important',
              WebkitTextFillColor: '#f16253 !important',
              cursor: isComponentDisabled ? 'not-allowed' : 'pointer',
            },
            '& .MuiSelect-select.Mui-disabled': {
              color: '#f16253 !important',
              WebkitTextFillColor: '#f16253 !important',
              cursor: 'not-allowed',
            },
            '& .MuiSelect-icon': {
              color: '#f16253 !important'
            },
            '& .MuiSelect-icon.Mui-disabled': {
              color: '#f16253 !important'
            },
            pointerEvents: 'auto'
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 400,
              },
            },
          }}
          renderValue={renderSelectValue}
        >
          {scope.editing && scope.partList?.length > 0 && (
            <MenuItem
              key="SELECT_ALL"
              value="SELECT_ALL"
              sx={{
                borderBottom: '1px solid #e0e0e0',
                fontWeight: 'bold',
                backgroundColor: '#f5f5f5',
                '&:hover': {
                  backgroundColor: '#e3f2fd'
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Checkbox
                  checked={(() => {
                    const isNewScope = scope.isNewRow;
                    const selectableParts = getSelectableParts(scope.partList || [], isNewScope)
                      .filter(part => !isPartDisabled(part, isNewScope));
                    const selectableIds = selectableParts.map(p => p.bom_id);
                    const selectedSelectable = selectableIds.filter(id => (scope.parts || []).includes(id));
                    return selectedSelectable.length === selectableIds.length && selectableIds.length > 0;
                  })()}
                  indeterminate={(() => {
                    const isNewScope = scope.isNewRow;
                    const selectableParts = getSelectableParts(scope.partList || [], isNewScope)
                      .filter(part => !isPartDisabled(part, isNewScope));
                    const selectableIds = selectableParts.map(p => p.bom_id);
                    const selectedSelectable = selectableIds.filter(id => (scope.parts || []).includes(id));
                    return selectedSelectable.length > 0 && selectedSelectable.length < selectableIds.length;
                  })()}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" fontWeight={600}>
                  Select All ({(() => {
                    const isNewScope = scope.isNewRow;
                    return getSelectableParts(scope.partList || [], isNewScope)
                      .filter(part => !isPartDisabled(part, isNewScope)).length;
                  })()} selectable parts)
                </Typography>
              </Box>
            </MenuItem>
          )}

          {scope.partList?.map(part => {
            const isNewScope = scope.isNewRow;
            const partDisabled = isPartDisabled(part, isNewScope);
            const partChecked = (part.fully_assigned && !part.scope_assigned)
                                  ? true
                                  : isPartChecked(part, scope.parts || []);

            return (
              <MenuItem 
                key={part.bom_id} 
                value={part.bom_id}
                disabled={partDisabled}
                sx={{
                  opacity: partDisabled ? 0.6 : 1,
                  backgroundColor: partDisabled ? '#f5f5f5' : 'inherit'
                }}
              >
                <Checkbox
                  checked={partChecked}
                  disabled={partDisabled}
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={500}
                    sx={{ 
                      color: partDisabled ? 'text.secondary' : (partChecked && !partDisabled ? '#f16253' : 'text.primary'),
                      fontWeight: partChecked && !partDisabled ? 600 : 500
                    }}
                  >
                    {part.part_name}
                    {part.scope_assigned && (
                      <Chip 
                        label="Scope Assigned" 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    {part.fully_assigned && (
                      <Chip 
                        label="Fully Assigned" 
                        size="small" 
                        color="success" 
                        variant="outlined"
                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {part.description}
                  </Typography>
                </Box>
              </MenuItem>
            );
          }) || []}
        </Select>
      
        {scope.isLoadingParts && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Loading parts...
          </Typography>
        )}
        {scope.isLoadingDropdownData && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Loading dropdown data...
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default PartsSelector;